import React, { useState, useEffect } from 'react';
import { FaCrown, FaCheck, FaChartLine, FaRocket, FaCreditCard, FaDownload } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';

const Subscription = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, usageRes] = await Promise.all([
        api.get('/subscription/plans'),
        api.get('/subscription/usage')
      ]);
      setPlans(plansRes.data.data.plans);
      setUsage(usageRes.data.data);
      
      // Load invoices if available
      try {
        const invoicesRes = await api.get('/subscription/invoices');
        setInvoices(invoicesRes.data.data.invoices || []);
      } catch (err) {
        // Invoices endpoint might not exist yet
      }
    } catch (err) {
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api.post('/subscription/upgrade', { plan: planId });
      if (response.data.data.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl;
      } else {
        setSuccess('Plan updated successfully!');
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upgrade plan');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await api.post('/subscription/cancel');
      setSuccess('Subscription cancelled. You can continue using until the end of billing period.');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading subscription data..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Subscription & Billing</h1>
        {user?.subscription?.plan === 'pro' && (
          <div className="flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg">
            <FaCrown className="mr-2" />
            Pro Member
          </div>
        )}
      </div>

      {/* Messages */}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {/* Current Plan */}
      <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-6 rounded-lg shadow-lg text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 mb-1">Current Plan</p>
            <h2 className="text-3xl font-bold capitalize">{user?.subscription?.plan || 'starter'}</h2>
            <p className="text-sm opacity-90 mt-2">
              Status: <span className="font-medium capitalize">{user?.subscription?.status || 'Active'}</span>
            </p>
          </div>
          <div className="text-right">
            {user?.subscription?.currentPeriodEnd && (
              <>
                <p className="text-sm opacity-90">Next billing date</p>
                <p className="text-lg font-semibold">
                  {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Usage */}
      {usage && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <FaChartLine className="text-blue-600 text-xl mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Usage This Month</h2>
          </div>
          <div className="space-y-6">
            {/* Products */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Products</span>
                <span className="text-sm font-bold text-gray-900">
                  {usage.usage.products.current} / {usage.usage.products.unlimited ? '∞' : usage.usage.products.limit}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    usage.usage.products.percentage > 80 ? 'bg-red-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${Math.min(usage.usage.products.percentage, 100)}%` }}
                ></div>
              </div>
              {usage.usage.products.percentage > 80 && !usage.usage.products.unlimited && (
                <p className="text-xs text-red-600 mt-1">
                  You're approaching your limit. Consider upgrading to Pro for unlimited products.
                </p>
              )}
            </div>

            {/* Chat Messages */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">AI Chat Messages</span>
                <span className="text-sm font-bold text-gray-900">
                  {usage.usage.chatMessages.current} / {usage.usage.chatMessages.unlimited ? '∞' : usage.usage.chatMessages.limit}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    usage.usage.chatMessages.percentage > 80 ? 'bg-red-500' : 'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(usage.usage.chatMessages.percentage, 100)}%` }}
                ></div>
              </div>
              {usage.usage.chatMessages.resetsOn && (
                <p className="text-xs text-gray-500 mt-1">
                  Resets on: {new Date(usage.usage.chatMessages.resetsOn).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {plans.map((plan) => {
            const isPro = plan.id === 'pro';
            const currentPlan = user?.subscription?.plan || 'starter';
            const isCurrent = plan.id === currentPlan;
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white p-6 rounded-lg shadow-lg transition-all ${
                  isCurrent ? 'ring-2 ring-blue-600' : 'hover:shadow-xl'
                } ${isPro ? 'border-2 border-yellow-400' : ''}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  {isCurrent && (
                    <span className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                      Current Plan
                    </span>
                  )}
                </div>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600">/month</span>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <FaCheck className="text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {!isCurrent && (
                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={upgrading}
                    className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                      isPro
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    <FaRocket className="mr-2" />
                    {upgrading ? 'Processing...' : isPro ? 'Upgrade to Pro' : 'Switch to Starter'}
                  </button>
                )}
                
                {isCurrent && currentPlan === 'pro' && (
                  <button
                    onClick={handleCancelSubscription}
                    className="w-full px-6 py-3 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors"
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing History */}
      {invoices.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <FaCreditCard className="text-blue-600 text-xl mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Billing History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(invoice.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${(invoice.amount / 100).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <a
                        href={invoice.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <FaDownload className="mr-1" />
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
